import { checkoutTrackingMetadata, checkoutSuccessUrl } from '@/lib/checkoutTracking';
import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { Check, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ScrollReveal from "@/components/landing/ScrollReveal";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getPromoSlug } from "@/lib/promoSlug";
import { trackEvent, MIXPANEL_EVENTS } from "@/utils/mixpanel";
import { pushDataLayerEvent } from "@/utils/googleAnalytics";
import { getAttributionParams } from "@/lib/attribution";

// Download URL + click tracking live in src/lib/desktopDownload.ts now (so the
// adaptive DownloadCta can use them without importing this whole section);
// re-exported here verbatim for every pre-existing import site.
export { MACOS_DOWNLOAD_URL, trackDownloadClick } from "@/lib/desktopDownload";
import DownloadCta from "@/components/desktop/DownloadCta";

// grain texture overlaid on the feature panels' gradients (SVG turbulence)
export const NOISE_TEXTURE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

// every plan includes the same core capabilities — only credits scale up
const SHARED_FEATURES = [
  "Voice Chat",
  "Learn Mode",
  "Auto Mode",
  "Full access to education platform",
];

// Vovy Desktop Pro/Max subscription price IDs (INC Stripe account — same account
// that backs the existing Go/Learn embedded checkout). The stripe-webhook-inc
// getDesktopPlan() keys off the price id (not tierName) to grant desktop credits:
// Pro -> users.desktop_monthly_limit=500, Max -> 2000, plus
// active_subscription_tier='fullstack'. Cleared on cancel.
const DESKTOP_PRO_MONTHLY_PRICE_ID = "price_1Tv9PDDXb9nja9yKCiK0xtDe"; // $30/mo  -> 500 credits
const DESKTOP_PRO_YEARLY_PRICE_ID = "price_1Tv9PDDXb9nja9yKQV67LuTY"; // $240/yr -> 500 credits
const DESKTOP_MAX_MONTHLY_PRICE_ID = "price_1Tv9PEDXb9nja9yK3KY9IG5s"; // $100/mo -> 2000 credits
const DESKTOP_MAX_YEARLY_PRICE_ID = "price_1Tv9PEDXb9nja9yKDTFqlQ6l"; // $960/yr -> 2000 credits

/**
 * Real USD charged per desktop plan — the single source of truth for every
 * conversion value we report (purchase_started here, purchase/purchase_complete
 * on /welcome). Yearly is the ANNUAL amount actually billed, not the monthly
 * equivalent shown on the card. Keep in lockstep with the Stripe price IDs above.
 */
export const DESKTOP_PLAN_VALUE = {
  pro: { monthly: 30, yearly: 240 },
  max: { monthly: 100, yearly: 960 },
} as const satisfies Record<string, { monthly: number; yearly: number }>;

export type DesktopTierKey = keyof typeof DESKTOP_PLAN_VALUE;

/** Narrow an untrusted ?plan= value to a real desktop tier (defaults to pro). */
export const toDesktopTierKey = (value: string | null | undefined): DesktopTierKey =>
  (value || '').toLowerCase() === 'max' ? 'max' : 'pro';

interface PricingTier {
  name: string;
  monthlyPrice: string;
  yearlyPrice: string;
  yearlyBilled: string;
  credits: string;
  desc: string;
  cta: string;
  featured: boolean;
  /** Loading/analytics key for paid tiers. Absent = Free tier (download CTA). */
  checkoutKey?: string;
  /** INC Stripe price IDs. Absent on Free, which downloads the DMG instead. */
  monthlyPriceId?: string;
  yearlyPriceId?: string;
}

// the two plans — credits are the messages you send Vovy each month.
// Yearly billing follows the site's standard ~2-months-free discount.
const PRICING_TIERS: PricingTier[] = [
  {
    name: "Pro",
    monthlyPrice: "$30",
    yearlyPrice: "$20",
    yearlyBilled: "billed $240/year",
    credits: "500 credits / month",
    desc: "For builders working with Vovy every week.",
    cta: "Get Pro",
    featured: true,
    checkoutKey: "pro",
    monthlyPriceId: DESKTOP_PRO_MONTHLY_PRICE_ID,
    yearlyPriceId: DESKTOP_PRO_YEARLY_PRICE_ID,
  },
  {
    name: "Max",
    monthlyPrice: "$100",
    yearlyPrice: "$80",
    yearlyBilled: "billed $960/year",
    credits: "2,000 credits / month",
    desc: "For builders who want Vovy doing the heavy lifting.",
    cta: "Get Max",
    featured: false,
    checkoutKey: "max",
    monthlyPriceId: DESKTOP_MAX_MONTHLY_PRICE_ID,
    yearlyPriceId: DESKTOP_MAX_YEARLY_PRICE_ID,
  },
];

// Biggest yearly discount across the paid tiers, shown as the toggle's teaser badge.
const MAX_YEARLY_SAVINGS_PCT = Math.max(
  ...PRICING_TIERS.map((t) => {
    const m = parseInt(t.monthlyPrice.replace(/\D/g, ""), 10);
    const y = parseInt(t.yearlyPrice.replace(/\D/g, ""), 10);
    return m > 0 ? Math.round(((m - y) / m) * 100) : 0;
  }),
);

interface PricingSectionProps {
  /** Adapt the heading/toggle text for a dark (sky) page background — the
   * cards themselves stay identical to the desktop page. */
  onDark?: boolean;
  /** Reserve the landing pad above the heading where the flying VovyGuide
   * parks (desktop page only — other pages have no guide). */
  landingPad?: boolean;
}

/** The desktop-app pricing — shared verbatim by /, /go, and /learn. */
const PricingSection = ({ onDark = false, landingPad = false }: PricingSectionProps) => {
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  // One-hop signup resume: fire the stored checkout intent at most once.
  const resumeFired = useRef(false);

  // Most visitors see pricing on the homepage, not /pricing (which tracks its
  // own view). Fire one "Pricing Page Viewed" when the section scrolls into view.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || window.location.pathname === "/pricing" || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        trackEvent(MIXPANEL_EVENTS.PRICING_PAGE_VIEWED, { ui_surface: "landing_section", path: window.location.pathname });
        io.disconnect();
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Open Stripe checkout for a paid desktop tier (Pro/Max). Reuses the exact
  // embedded-checkout mechanism from GoCheckoutPricingCards: create-checkout-session
  // returns a clientSecret which /checkout (GoCheckout) mounts via <EmbeddedCheckout>.
  const createCheckoutSession = async (
    priceId: string,
    tierKey: string,
    tierName: string,
    billingPeriod: "monthly" | "yearly",
  ) => {
    // Signed-out: carry the intended plan so checkout auto-resumes after auth
    // (one-hop — no second click). AuthCallback honors the returnTo, and the
    // resume effect below reads+clears the stored intent once signed in.
    if (!user?.id) {
      try {
        sessionStorage.setItem(
          "vovy_checkout_intent",
          JSON.stringify({ plan: tierKey, billing: billingPeriod }),
        );
      } catch {
        /* private mode / storage disabled — resume simply won't fire */
      }
      navigate("/signup?returnTo=/pricing?resume=1");
      return;
    }
    setLoadingTier(tierKey);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userToken = session?.access_token;
      if (!userToken) {
        toast({ title: "Please sign in again", description: "Your session has expired.", variant: "destructive" });
        return;
      }
      const response = await fetch("https://api.vcodinator.com/functions/v1/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${userToken}` },
        body: JSON.stringify({
          ...checkoutTrackingMetadata(),
          priceId,
          userId: user.id,
          // Cosmetic only — the webhook grants desktop credits by price id, not tierName.
          tierName: `Vovy ${tierName}`,
          billingPeriod,
          // billing rides along so /welcome can report the right purchase value.
          successUrl: checkoutSuccessUrl(`${window.location.origin}/welcome?subscribed=true&plan=${tierKey}&billing=${billingPeriod}`),
          cancelUrl: `${window.location.origin}/#pricing`,
          mode: "subscription",
          uiMode: "embedded",
          promoSlug: getPromoSlug() || undefined,
          // Show Stripe's "Add promotion code" field so Pro/Max buyers can type
          // a discount code. The edge fn's if/else means an auto-applied promo
          // slug still wins — this only surfaces the manual field when no slug
          // resolved, so the two never collide (Stripe rejects both at once).
          allowPromotionCodes: true,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || "Failed to create checkout session");
      }
      const data = await response.json();
      if (data.clientSecret) {
        const value = DESKTOP_PLAN_VALUE[toDesktopTierKey(tierKey)][billingPeriod];
        trackEvent(MIXPANEL_EVENTS.CHECKOUT_STARTED, {
          ui_surface: "desktop_pricing",
          plan: tierKey,
          mode: "subscription",
          billingPeriod,
          value,
          ...getAttributionParams(),
        });
        // GTM purchase-initiation event. Same name the Learn flow uses
        // (LearnCheckout.tsx), same meaning: a Stripe checkout session exists
        // but no payment has happened yet. `plan` is the billing period on both
        // surfaces; `tier` distinguishes Pro from Max, whose values differ.
        pushDataLayerEvent("purchase_started", {
          plan: billingPeriod,
          tier: tierKey,
          value,
          currency: "USD",
          surface: "desktop_pricing",
        });
        navigate("/checkout", { state: { clientSecret: data.clientSecret } });
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Invalid response from checkout");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "An error occurred";
      toast({ title: "Checkout failed", description: msg, variant: "destructive" });
    } finally {
      setLoadingTier(null);
    }
  };

  const startDesktopCheckout = (tier: PricingTier) => {
    if (!tier.checkoutKey) return;
    const priceId = billing === "yearly" ? tier.yearlyPriceId : tier.monthlyPriceId;
    if (!priceId) return;
    createCheckoutSession(priceId, tier.checkoutKey, tier.name, billing);
  };

  // Auto-resume the checkout a signed-out visitor started before signing up.
  // Fires when the user is signed in AND either the URL carries ?resume=1 or a
  // stored `vovy_checkout_intent` exists. The ref + immediate clear guarantee it
  // runs exactly once (StrictMode double-invoke, re-renders, and repeat mounts
  // all no-op after the first success). Uses the intent's own billing period
  // rather than the toggle's live state, which wouldn't be updated yet.
  useEffect(() => {
    if (!user?.id) return;
    if (resumeFired.current) return;

    let intent: { plan?: string; billing?: "monthly" | "yearly" } | null = null;
    try {
      const raw = sessionStorage.getItem("vovy_checkout_intent");
      if (raw) intent = JSON.parse(raw);
    } catch {
      intent = null;
    }
    if (!intent) return;
    // Guard is redundant given a stored intent, but honors the ?resume=1 signal.
    if (searchParams.get("resume") !== "1" && !intent.plan) return;

    const tier = PRICING_TIERS.find((t) => t.checkoutKey === intent!.plan);
    resumeFired.current = true;
    sessionStorage.removeItem("vovy_checkout_intent");
    if (!tier?.checkoutKey) return;

    const period: "monthly" | "yearly" = intent.billing === "yearly" ? "yearly" : "monthly";
    setBilling(period);
    const priceId = period === "yearly" ? tier.yearlyPriceId : tier.monthlyPriceId;
    if (!priceId) return;
    createCheckoutSession(priceId, tier.checkoutKey, tier.name, period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, searchParams]);

  return (
    <section ref={sectionRef} id="pricing" className="scroll-mt-24 px-[20px] sm:px-6 lg:px-8 py-12 md:py-24">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          {/* the rocket-riding Vovy locks in here, right above the heading */}
          {landingPad && (
            <div
              data-vovy-stop="pricing"
              className="mx-auto mb-3 md:mb-10 flex items-center justify-center h-[11.5rem] w-[9.75rem] md:h-[18.75rem] md:w-[16rem]"
            />
          )}
          <h2
            className={`text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] ${
              onDark ? "text-white" : "text-foreground"
            }`}
          >
            Simple pricing.
          </h2>
          <p className={`mt-6 text-lg ${onDark ? "text-white/80" : "text-muted-foreground"}`}>
            7-day free trial with 100 credits
          </p>

          {/* Monthly / yearly toggle */}
          <div
            className={`mt-8 inline-flex items-center gap-1 rounded-full p-1.5 ${
              onDark ? "bg-white/15 backdrop-blur" : "bg-slate-100"
            }`}
          >
            {(["monthly", "yearly"] as const).map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => setBilling(period)}
                className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${
                  billing === period
                    ? "bg-white text-slate-900 shadow-sm"
                    : onDark
                      ? "text-white/70 hover:text-white"
                      : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {period === "monthly" ? "Monthly" : "Yearly"}
                {period === "yearly" && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                      billing === "yearly"
                        ? "bg-primary/10 text-primary"
                        : onDark
                          ? "bg-white/20 text-white/80"
                          : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    Save up to {MAX_YEARLY_SAVINGS_PCT}%
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        <ScrollReveal>
          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto items-stretch">
            {PRICING_TIERS.map((tier) => {
              const price = billing === "yearly" ? tier.yearlyPrice : tier.monthlyPrice;
              // Free doesn't advertise the education platform — only the paid tiers.
              const tierFeatures =
                tier.name === "Free"
                  ? SHARED_FEATURES.filter((f) => f !== "Full access to education platform")
                  : SHARED_FEATURES;
              const allFeatures = [tier.credits, ...tierFeatures];
              // Shared CTA look — identical for the download link and the checkout button.
              const ctaStyle = tier.featured
                ? {
                    ["--gamey-shadow" as string]: "#bcdcf2",
                    ["--gamey-shadow-hover" as string]: "#9cc8ea",
                  }
                : {
                    ["--gamey-shadow" as string]: "#0369a1",
                    ["--gamey-shadow-hover" as string]: "#075985",
                  };
              const ctaClassName = `btn-gamey inline-flex w-full items-center justify-center gap-2 rounded-xl sm:rounded-2xl px-4 py-2.5 sm:px-6 sm:py-3 text-[13px] sm:text-sm font-bold uppercase tracking-wide transition-colors ${
                tier.featured
                  ? "bg-white hover:bg-slate-50 text-[#0B6BB0]"
                  : "bg-primary hover:bg-primary/90 text-white"
              }`;
              return (
                <div
                  key={tier.name}
                  className={`relative rounded-2xl sm:rounded-3xl overflow-hidden p-4 sm:p-8 flex flex-col ${
                    tier.featured
                      ? "bg-gradient-to-br from-[#7CC3F0] to-[#2E92D6] text-white shadow-[0_28px_70px_-24px_rgba(20,80,180,0.5)]"
                      : "bg-white border border-slate-200"
                  }`}
                >
                  {tier.featured && (
                    <>
                      {/* film-grain noise, matching the feature panels */}
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 opacity-[0.16] mix-blend-soft-light"
                        style={{ backgroundImage: NOISE_TEXTURE }}
                      />
                      <span className="absolute top-3.5 right-3.5 sm:top-5 sm:right-5 rounded-full bg-white/20 backdrop-blur px-2.5 py-0.5 sm:px-3 sm:py-1 text-[11px] sm:text-xs font-bold text-white">
                        Most popular
                      </span>
                    </>
                  )}
                  <div className="relative flex flex-col h-full">
                    <p className={`text-xs sm:text-sm font-bold uppercase tracking-wider ${tier.featured ? "text-white/85" : "text-slate-500"}`}>
                      {tier.name}
                    </p>
                    <p className="mt-2 sm:mt-3 flex items-baseline gap-1">
                      <span className={`text-3xl sm:text-5xl font-bold tracking-tight ${tier.featured ? "text-white" : "text-slate-900"}`}>
                        {price}
                      </span>
                      {price !== "$0" && (
                        <span className={`text-sm sm:text-base font-medium ${tier.featured ? "text-white/80" : "text-slate-500"}`}>
                          /month
                        </span>
                      )}
                    </p>
                    {billing === "yearly" && tier.yearlyBilled && (
                      <p className={`mt-1 text-[11px] sm:text-xs font-medium ${tier.featured ? "text-white/70" : "text-slate-400"}`}>
                        {tier.yearlyBilled}
                      </p>
                    )}
                    <p className={`mt-2.5 sm:mt-4 text-[13px] sm:text-sm leading-snug sm:leading-relaxed ${tier.featured ? "text-white/85" : "text-slate-600"}`}>
                      {tier.desc}
                    </p>

                    {/* Features — credits first, then the shared core capabilities */}
                    <ul className="mt-3.5 sm:mt-5 space-y-2 sm:space-y-2.5">
                      {allFeatures.map((feature) => (
                        <li
                          key={feature}
                          className={`flex items-start gap-2 text-[13px] sm:text-sm font-medium ${tier.featured ? "text-white" : "text-slate-900"}`}
                        >
                          <span className={`mt-0.5 flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full shrink-0 ${tier.featured ? "bg-white/20" : "bg-primary/10"}`}>
                            <Check className={`h-2.5 w-2.5 sm:h-3 sm:w-3 ${tier.featured ? "text-white" : "text-primary"}`} />
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto pt-4 sm:pt-8">
                      {tier.checkoutKey ? (
                        // Pro / Max — real Stripe checkout (embedded, via /checkout).
                        <button
                          type="button"
                          onClick={() => startDesktopCheckout(tier)}
                          disabled={loadingTier === tier.checkoutKey}
                          style={ctaStyle}
                          className={`${ctaClassName} disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          {loadingTier === tier.checkoutKey ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading…
                            </>
                          ) : (
                            tier.cta
                          )}
                        </button>
                      ) : (
                        // Free — the adaptive CTA: download on a Mac, email
                        // the link on a phone, waitlist on Windows.
                        <DownloadCta
                          placement="pricing"
                          style={ctaStyle}
                          className={ctaClassName}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PricingSection;
